import { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import { Box, CircularProgress, Paper } from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';

// Material ui components
// Global components
import OperationCRIAsset from './OperationCRIAsset';
import OperationCRIGeneralData from './OperationCRIGeneralData';
import OperationInvestors from './OperationCRIInvestors';
import OperationCRILiability from './OperationCRILiability';
import OperationCRIResume from './OperationCRIResume';
import OperationCRIServiceProvider from './OperationCRIServiceProvider';
import { If } from '../../../components/If';
import OperationSubheader from '../../../components/OperationSubheader';
import MetaTags from '../../../components/Seo/MetaTags';
// Local components
import { TabSection } from '../../../components/TabSection';
import ipanema from '../../../graphql/clients/ipanema';
import { GET_CRI_CADASTRAL_DATA_BY_OPERATION_HASH_ID } from '../../../graphql/markets/cri/queries';
import OperationDocuments from '../OperationDocuments';
// Apollo client
import OperationRelatedArticles from '../OperationRelatedArticles';
import OperationSecondary from '../OperationSecondary';
import { OperationTimeline } from '../OperationTimeline';

export default function OperationCRI() {
  const { tab, hashId, market, slug } = useParams();
  const history = useHistory();
  const [value, setValue] = useState(
    parseInt(window.localStorage.getItem('operationTabIndex')) || 'resumo',
  );
  const tabs = [
    { label: 'Resumo', value: 'resumo' },
    { label: 'Dados Gerais', value: 'dados' },
    { label: 'Prestadores de Serviço', value: 'prestadores' },
    { label: 'Ativo', value: 'ativo' },
    { label: 'Passivo', value: 'passivo' },
    { label: 'Secundário', value: 'secundario' },
    { label: 'Investidores', value: 'investidores' },
    { label: 'Linha do Tempo', value: 'timeline' },
    { label: 'Documentos', value: 'documentos' },
    { label: 'Artigos Relacionados', value: 'artigos' },
  ];

  const { data, loading, error } = useQuery(
    GET_CRI_CADASTRAL_DATA_BY_OPERATION_HASH_ID,
    {
      variables: { hash: hashId },
      client: ipanema,
    },
  );

  const handleTabChange = (tabIndex) => {
    setValue(tabIndex);
    if (tabIndex === 'resumo') {
      history.push(`/operacao/${market}/${slug}/${hashId}`);
    } else {
      history.push(`/operacao/${market}/${slug}/${hashId}/${tabIndex}`);
    }
  };

  useEffect(() => {
    setValue(tab ?? 'resumo');
  }, []);

  useEffect(() => {
    window.localStorage.setItem('operationTabIndex', JSON.stringify(value));
    return function clean() {
      localStorage.removeItem('operationTabIndex');
    };
  }, [value]);

  if (loading) return <CircularProgress color="secondary" />;
  if (error) return null;

  const name = data?.getCriCadastralDataByOperationHashId?.operationName ?? '';

  return (
    <>
      <MetaTags
        title={`${name} - CRI`}
        description={`Encontre as principais informações sobre ${name}`}
      />
      <OperationSubheader
        title={name}
        market={market.toUpperCase()}
        breadcrumbs1="Certificados"
        breadcrumbs2={`${name} ${market.toUpperCase()}`}
      />
      <Box py={2}>
        <TabSection tabList={tabs} value={value} setValue={handleTabChange}>
          <Paper square>
            <If condition={value === 'resumo'}>
              <OperationCRIResume hashId={hashId} />
            </If>
            <If condition={value === 'dados'}>
              <OperationCRIGeneralData hashId={hashId} />
            </If>
            <If condition={value === 'prestadores'}>
              <OperationCRIServiceProvider hashId={hashId} />
            </If>
            <If condition={value === 'ativo'}>
              <OperationCRIAsset hashId={hashId} operationName={name} />
            </If>
            <If condition={value === 'passivo'}>
              <OperationCRILiability hashId={hashId} />
            </If>
            <If condition={value === 'secundario'}>
              <OperationSecondary hash={hashId} market={market.toUpperCase()} />
            </If>
            <If condition={value === 'documentos'}>
              <OperationDocuments hash={hashId} />
            </If>
            <If condition={value === 'artigos'}>
              <OperationRelatedArticles hash={hashId} />
            </If>
            <If condition={value === 'investidores'}>
              <OperationInvestors
                hash={hashId}
                market={market.toUpperCase()}
                operationName={name}
              />
            </If>
            <If condition={value === 'timeline'}>
              <OperationTimeline hashId={hashId} />
            </If>
          </Paper>
        </TabSection>
      </Box>
    </>
  );
}
